import React, { useEffect, useMemo, useState, Suspense, lazy } from 'react';
import axios from 'axios';
import url from '../../url.js';
import { AuthContext } from '../../contexts/AuthContext';
import { useContext } from 'react';
import '../../styles/Main.css';
import { useNavigate } from 'react-router-dom';
import { findroomByInfo } from '../../utils/findRoomMode';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Menu, MenuItem, IconButton, Box, Typography, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { SearchCode, Calendar, Users, Settings, Bot, EyeOff, Share2, Trash2, RotateCcw, Zap, Wrench } from 'lucide-react';
import { CreateActivityForm } from '../../components/CreateActivityForm';
import ActivityInfo from '../../models/ActivityInfo';
import { normalizeSettingsForInitialNodes } from '../../utils/activityInfoAccessors';
import ActivitySettingsDialog from '../../components/Admin/ActivitySettingsDialog';
import PartnerManager from '../../components/PartnerManager';
import { activityService } from '../../services/activityService';
import RoomSettingsChatroomIcon from '../../assets/RoomSettings/chatroom.png';
import RoomSettingsMindmapIcon from '../../assets/RoomSettings/mindmap.png';
import ForumIcon from '../../assets/返回論壇icon.png';
import siteLogo from '../../assets/logo v4.png';
import { QRCodeSVG } from 'qrcode.react';
import Homepage from '../Homepage/Homepage';
import Studio from '../Studio';
import UnreadIndicator from '../../components/UnreadIndicator';
import useUnreadMessages from '../../hooks/useUnreadMessages';
import semanticColors from '../../styles/semanticColors';
import colors from '../../styles/colors.js';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '../../i18n';
import { getActivityRequestConfig } from '../../utils/activityRequestConfig';

const ModeComponents = {
  chatroom: lazy(() => import('../ForumMode/ForumChatRoom')),
  sticker: lazy(() => import('../ForumMode/ForumSticker')),
  'tagged-forum': lazy(() => import('../ForumMode/TagBasedForum')),
  workspace: lazy(() => import('../ForumMode/StickerWorkspace')),
};

export default function Main() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('main');

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
  };
  
  // 在組件頂層讀取一次常用的 localStorage 值
  const userId = useMemo(() => localStorage.getItem('userId'), []);
  const jwtToken = useMemo(() => localStorage.getItem('jwtToken'), []);
  
  const [activities, setActivities] = useState([]); // [{ activity, joinedGroups?, allGroups?, isOwner }]
  const [selectedActivityId, setSelectedActivityId] = useState(null);
  const [groups, setGroups] = useState([]); // groups to display for selected activity
  const [loading, setLoading] = useState(true);
  const [collapseSidebar, setCollapseSidebar] = useState(true);
  const [collapseActivity, setCollapseActivity] = useState(false);
  const [collapseGroup, setCollapseGroup] = useState(true);
  const [embeddedMode, setEmbeddedMode] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [joinOpen, setJoinOpen] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [exploreActivities, setExploreActivities] = useState([]);
  const [exploreLoading, setExploreLoading] = useState(false);
  const [exploreError, setExploreError] = useState('');
  const [exploreSortBy, setExploreSortBy] = useState('latest');
  const [exploreDetailOpen, setExploreDetailOpen] = useState(false);
  const [selectedExploreActivity, setSelectedExploreActivity] = useState(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [settingsForActivityId, setSettingsForActivityId] = useState(null);
  const [openCreateGroupDialog, setOpenCreateGroupDialog] = useState(false);
  const [numGroupsToCreate, setNumGroupsToCreate] = useState(1);
  const [creatingGroups, setCreatingGroups] = useState(false);
  const [openSettingOfActivity, setOpenSettingOfActivity] = useState(false);
  // ❌ 移除 roomSetting - 由對話框內部管理
  // ❌ 移除 currentActivityForSettings - 不再需要
  
  // 檢查是否為首次登入，如果是則導向快速開始頁面
  const getInitialView = () => {
    const hasVisitedBefore = localStorage.getItem('hasVisitedApp');
    if (!hasVisitedBefore) {
      // 標記用戶已經訪問過
      localStorage.setItem('hasVisitedApp', 'true');
      return 'templates'; // 首次登入顯示快速開始
    }
    return 'activity'; // 之後的登入顯示活動列表
  };
  
  const [currentView, setCurrentView] = useState(getInitialView()); // 'activity' | 'explore' | 'partners' | 'templates'
  const [hasAutoSelectedGroup, setHasAutoSelectedGroup] = useState(false); // 防止重複自動選擇
  const [inviteAnchorEl, setInviteAnchorEl] = useState(null);
  const [inviteForJoinCode, setInviteForJoinCode] = useState('');
  const [inviteForGroup, setInviteForGroup] = useState(null); // 保存當前分享的小組
  const [recycleBinOpen, setRecycleBinOpen] = useState(false);
  const [deletedActivities, setDeletedActivities] = useState([]);
  const [hiddenActivities, setHiddenActivities] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [isPortrait, setIsPortrait] = useState(false);
  const { hasUnreadMap, fetchUnreadStatus, updateLastReadTime, markAsReadLocal } = useUnreadMessages();

  const formatPreviewNodeText = (value) => {
    if (value == null) return '';

    const decodeHtml = (str) => {
      if (typeof window === 'undefined') return str;
      const textarea = document.createElement('textarea');
      textarea.innerHTML = str;
      return textarea.value;
    };

    const htmlToText = (str) => {
      const decoded = decodeHtml(String(str));
      return decoded
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|section|article|li|ul|ol|h[1-6])>/gi, '\n')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\u00a0/g, ' ')
        .replace(/[ \t]{2,}/g, ' ')
        .replace(/ *\n */g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    };

    const stringifyQuestionnaire = (obj) => {
      const description = typeof obj.description === 'string' ? obj.description.trim() : '';
      const questions = Array.isArray(obj.questions) ? obj.questions : [];
      const questionLines = questions
        .map((q, idx) => {
          const text = typeof q?.text === 'string' ? q.text.trim() : '';
          return text ? `${idx + 1}. ${text}` : null;
        })
        .filter(Boolean);

      const blocks = [];
      if (description) blocks.push(description);
      if (questionLines.length > 0) blocks.push(questionLines.join('\n'));
      return blocks.join('\n\n').trim();
    };

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return '';

      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed && typeof parsed === 'object' && Array.isArray(parsed.questions)) {
            const readable = stringifyQuestionnaire(parsed);
            if (readable) return readable;
          }
        } catch {
          // Keep original string path when it's not valid JSON.
        }
      }

      return htmlToText(trimmed);
    }

    if (typeof value === 'object') {
      if (Array.isArray(value.questions)) {
        const readable = stringifyQuestionnaire(value);
        if (readable) return readable;
      }
      return htmlToText(JSON.stringify(value));
    }

    return String(value);
  };

  const fetchExploreActivities = async () => {
    setExploreLoading(true);
    try {
      const result = await activityService.getPublicActivities();
      if (result.success) {
        setExploreActivities(result.data || []);
        setExploreError('');
      } else {
        setExploreActivities([]);
        setExploreError(result.error || '無法載入公開活動');
      }
    } catch (error) {
      console.error('載入公開活動失敗:', error);
      setExploreActivities([]);
      setExploreError('無法載入公開活動');
    } finally {
      setExploreLoading(false);
    }
  };

  const sortedExploreActivities = useMemo(() => {
    const list = [...exploreActivities];
    if (exploreSortBy === 'latest') {
      list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }
    return list;
  }, [exploreActivities, exploreSortBy]);

  const openExploreActivity = (activity) => {
    setSelectedExploreActivity(activity);
    setExploreDetailOpen(true);
  };

  const joinActivityByCode = async (code, targetActivityId = null) => {
    if (!code || !userId) {
      alert(t('alert.enterCode'));
      return false;
    }

    try {
      const payload = { userId };
      await axios.put(`${url.backendHost}api/groups/${code}/join`, payload, getActivityRequestConfig());
      const merged = await fetchAndMergeActivities();
      let matchedActivityId = null;

      if (targetActivityId && merged.some(item => Number(item.activity.id) === Number(targetActivityId))) {
        matchedActivityId = Number(targetActivityId);
      } else {
        const matchedActivity = merged.find((item) =>
          (item.joinedGroups || []).some((grp) => grp.joinCode === code)
        );
        matchedActivityId = matchedActivity ? Number(matchedActivity.activity.id) : null;
      }

      setActivities(merged);
      setJoinOpen(false);
      setJoinCode('');

      try {
        const renewResponse = await axios.get(`${url.backendHost}api/users/renew`, getActivityRequestConfig());
        if (renewResponse.data.jwtToken) {
          localStorage.setItem('jwtToken', renewResponse.data.jwtToken);
        }
      } catch {}

      if (matchedActivityId) {
        setSelectedActivityId(matchedActivityId);
        setCollapseGroup(false);
        setCurrentView('activity');
      } else {
        setSelectedActivityId(null);
        setGroups([]);
        setCurrentView('activity');
      }

      alert(t('alert.joinSuccess'));
      return true;
    } catch (error) {
      console.error('加入活動失敗:', error);
      alert(t('alert.joinFail'));
      return false;
    }
  };

  // 刷新活動列表的函數
  const refreshActivities = async () => {
    try {
      const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : undefined;
      const merged = await fetchAndMergeActivities(headers);
      setActivities(merged);
      console.log('活動列表已刷新');
    } catch (error) {
      console.error('刷新活動列表失敗:', error);
    }
  };

  const fetchAndMergeActivities = async (headers = undefined) => {
    if (!userId) {
      console.warn('No userId found in localStorage');
      return [];
    }
    
    const joinedUrl = `${url.backendHost}api/activities/myJoined/${userId}`;
    const createdUrl = `${url.backendHost}api/activities/myActivity/${userId}`;
    const requestConfig = getActivityRequestConfig();
    if (headers) {
      requestConfig.headers = { ...headers, ...requestConfig.headers };
    }
    
    console.log('Fetching activities:', { joinedUrl, createdUrl, userId });
    
    try {
    const [joinedRes, createdRes] = await Promise.all([
        axios.get(joinedUrl, requestConfig),
        axios.get(createdUrl, requestConfig)
    ]);

      console.log('Activities fetched successfully:', { 
        joinedCount: joinedRes.data?.length || 0, 
        createdCount: createdRes.data?.length || 0 
      });

    const joined = Array.isArray(joinedRes.data) ? joinedRes.data : [];
    const created = Array.isArray(createdRes.data) ? createdRes.data : [];

    const byId = new Map();

    // Created activities (owner view)
    for (const act of created) {
      const id = act.id;
      const existing = byId.get(id) || { activity: act, joinedGroups: [], allGroups: [], isOwner: true };
      existing.activity = act; // prefer latest
      existing.isOwner = true;
      existing.allGroups = Array.isArray(act.Groups) ? act.Groups : existing.allGroups;
      // 如果是活動擁有者且有小組，也將小組添加到joinedGroups中
      if (existing.isOwner && existing.allGroups.length > 0) {
        existing.joinedGroups = existing.allGroups.slice(); // 複製所有小組
      }
      byId.set(id, existing);
    }

    // Joined activities (may have multiple groups per activity)
    for (const entry of joined) {
      const act = entry?.ActivityGroup?.Activity;
      const grp = entry?.ActivityGroup?.Group;
      if (!act || !grp) continue;
      const id = act.id;
      const existing = byId.get(id) || { activity: act, joinedGroups: [], allGroups: [], isOwner: act.userId === Number(userId) };
      existing.activity = act;
      existing.joinedGroups = existing.joinedGroups || [];
      if (!existing.joinedGroups.find(g => g.id === grp.id)) {
        existing.joinedGroups.push(grp);
      }
      byId.set(id, existing);
    }

    return Array.from(byId.values()).sort((a, b) => new Date(b.activity.createdAt) - new Date(a.activity.createdAt));
    } catch (error) {
      console.error('Error in fetchAndMergeActivities:', error);
      console.error('Error details:', error.response?.data || error.message);
      throw error; // 重新拋出錯誤，讓上層處理
    }
  };

  useEffect(() => {
    const fetchLists = async () => {
      try {
        setLoading(true);
        
        // 先刷新token
        try {
          const renewResponse = await axios.get(`${url.backendHost}api/users/renew`, { withCredentials: true });
          if (renewResponse.data.jwtToken) {
            localStorage.setItem('jwtToken', renewResponse.data.jwtToken);
            console.log('JWT token 已更新');
          }
        } catch (tokenError) {
          console.error('刷新 token 失敗:', tokenError);
        }
        
        // 使用最新的token
        const currentToken = localStorage.getItem('jwtToken');
        const headers = currentToken ? { Authorization: `Bearer ${currentToken}` } : undefined;
        const merged = await fetchAndMergeActivities(headers);
        setActivities(merged);
        
        // 檢查 localStorage 中是否有預設的活動和小組 ID
        const savedActivityId = localStorage.getItem('activityId');
        const savedGroupId = localStorage.getItem('groupId');
        
        if (savedActivityId && merged.find(item => item.activity.id === parseInt(savedActivityId))) {
          
          setSelectedActivityId(parseInt(savedActivityId));
          
          // 如果有預設小組 ID，稍後在 loadGroupsForSelected 完成後設置
          if (savedGroupId) {
            
          }
        }
      } catch (e) {
        console.error('Error fetching activities:', e);
        console.error('Error details:', e.response?.data || e.message);
        // 即使失敗也要顯示錯誤信息，讓用戶知道發生了什麼
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };
    fetchLists();
  }, []);

  // 監聽私訊申請成功事件，自動刷新活動列表
  useEffect(() => {
    const handleDmApplicationSuccess = () => {
      console.log('檢測到私訊申請成功，刷新活動列表');
      refreshActivities();
    };

    window.addEventListener('dmApplicationSuccess', handleDmApplicationSuccess);

    return () => {
      window.removeEventListener('dmApplicationSuccess', handleDmApplicationSuccess);
    };
  }, [jwtToken]);

  // 監聽頁面可見性變化，當切換回頁籤時刷新token和資料
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        console.log('頁面變為可見，重新刷新 token 和資料');
        
        // 刷新token
        try {
          const renewResponse = await axios.get(`${url.backendHost}api/users/renew`, { withCredentials: true });
          if (renewResponse.data.jwtToken) {
            localStorage.setItem('jwtToken', renewResponse.data.jwtToken);
            console.log('JWT token 已更新');
          }
        } catch (tokenError) {
          console.error('刷新 token 失敗:', tokenError);
        }
        
        // 刷新活動列表
        await refreshActivities();
        
        // 如果有選中的活動，重新載入小組資料
        if (selectedActivityId) {
          const currentToken = localStorage.getItem('jwtToken');
          const headers = currentToken ? { Authorization: `Bearer ${currentToken}` } : undefined;
          try {
            const res = await axios.get(`${url.backendHost}api/activities/${selectedActivityId}`, { headers, withCredentials: true });
            const data = res.data;
            const sorted = (data?.Groups || []).slice().sort((a, b) => a.groupName.localeCompare(b.groupName, 'zh-Hant', { numeric: true }));
            setGroups(sorted);
          } catch (e) {
            console.error('重新載入小組失敗:', e);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [selectedActivityId]);

  // 監聽視圖切換，當切換回 activity 視圖時重新載入資料
  useEffect(() => {
    const reloadActivityData = async () => {
      if (currentView === 'activity') {
        console.log('切換回 activity 視圖，重新載入資料');
        
        // 刷新token
        try {
          const renewResponse = await axios.get(`${url.backendHost}api/users/renew`, { withCredentials: true });
          if (renewResponse.data.jwtToken) {
            localStorage.setItem('jwtToken', renewResponse.data.jwtToken);
            console.log('JWT token 已更新');
          }
        } catch (tokenError) {
          console.error('刷新 token 失敗:', tokenError);
        }
        
        // 使用最新的token刷新活動列表
        const currentToken = localStorage.getItem('jwtToken');
        const headers = currentToken ? { Authorization: `Bearer ${currentToken}` } : undefined;
        try {
          const merged = await fetchAndMergeActivities(headers);
          setActivities(merged);
          
          // 如果有選中的活動，依身份重新載入可見小組資料
          if (selectedActivityId) {
            const selected = merged.find(item => Number(item.activity.id) === Number(selectedActivityId));
            const availableGroups = selected?.isOwner
              ? (selected?.allGroups || [])
              : (selected?.joinedGroups || []);
            const sorted = availableGroups
              .slice()
              .sort((a, b) => a.groupName.localeCompare(b.groupName, 'zh-Hant', { numeric: true }));
            setGroups(sorted);
          }
        } catch (e) {
          console.error('重新載入活動資料失敗:', e);
        }
      }
    };

    // 只在從其他視圖切換回 activity 時執行
    if (currentView === 'activity') {
      reloadActivityData();
    }
  }, [currentView]);

  useEffect(() => {
    const loadGroupsForSelected = async (activityId) => {
      if (!activityId) return;
      const selected = activities.find(item => Number(item.activity.id) === Number(activityId));
      if (selected) {
        const availableGroups = selected.isOwner
          ? (selected.allGroups || [])
          : (selected.joinedGroups || []);
        const sorted = availableGroups.slice().sort((a, b) => a.groupName.localeCompare(b.groupName, 'zh-Hant', { numeric: true }));
        setGroups(sorted);
        
        // 自動選擇預設小組（如果有的話）
        const savedGroupId = localStorage.getItem('groupId');
        if (!hasAutoSelectedGroup && savedGroupId && sorted.find(g => g.id === parseInt(savedGroupId))) {
          
          const targetGroup = sorted.find(g => g.id === parseInt(savedGroupId));
          if (targetGroup) {
            handleEnterGroup(targetGroup);
            setHasAutoSelectedGroup(true);
          }
        }
        if (!selected.isOwner || sorted.length > 0) return;
      } else {
        setGroups([]);
        return;
      }
      try {
        const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : undefined;
        const res = await axios.get(`${url.backendHost}api/activities/${activityId}`, { headers, withCredentials: true });
        const data = res.data;
        const sorted = (data?.Groups || []).slice().sort((a, b) => a.groupName.localeCompare(b.groupName, 'zh-Hant', { numeric: true }));
        setGroups(sorted);
        
        // 自動選擇預設小組（如果有的話）
        const savedGroupId = localStorage.getItem('groupId');
        if (!hasAutoSelectedGroup && savedGroupId && sorted.find(g => g.id === parseInt(savedGroupId))) {
          
          const targetGroup = sorted.find(g => g.id === parseInt(savedGroupId));
          if (targetGroup) {
            handleEnterGroup(targetGroup);
            setHasAutoSelectedGroup(true);
          }
        }
      } catch (e) {
        setGroups([]);
      }
    };
    loadGroupsForSelected(selectedActivityId, activities);
  }, [selectedActivityId, activities]);

  // 全域拉取目前所有加入的小組未讀狀態（登入後即刻標註）
  useEffect(() => {
    if (!activities || activities.length === 0) return;
    const allGroupIds = new Set();
    activities.forEach((item) => {
      const groupsToUse = Array.isArray(item.joinedGroups) ? item.joinedGroups : [];
      groupsToUse.forEach((g) => {
        const gid = g.id ?? g.groupId;
        if (gid) allGroupIds.add(gid);
      });
    });
    if (allGroupIds.size > 0) {
      fetchUnreadStatus(Array.from(allGroupIds));
    }
  }, [activities, fetchUnreadStatus]);

  useEffect(() => {
    if (groups.length === 0) return;
    const ids = groups.map((g) => g.id ?? g.groupId).filter(Boolean);
    fetchUnreadStatus(ids);
  }, [groups, fetchUnreadStatus]);

  useEffect(() => {
    // clear embedded forum when switching activities
    setEmbeddedMode(null);
    setSelectedGroupId(null);
    setHasAutoSelectedGroup(false); // 重置自動選擇標記
  }, [selectedActivityId]);

  useEffect(() => {
    if (currentView === 'templates') {
      fetchExploreActivities();
    }
  }, [currentView]);

  // 處理點擊外部收合所有sidebar
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedElement = event.target;
      const isInsideSidebar = clickedElement.closest('.sidebar');
      const isInsideActivityList = clickedElement.closest('.activity-list');
      const isInsideGroupList = clickedElement.closest('.group-list');
      const isInsideMenu = clickedElement.closest('.MuiMenu-root') || clickedElement.closest('.MuiPopover-root');
      const isInsideDialog = clickedElement.closest('.MuiDialog-root');
      
      // 如果點擊的不是任何一個sidebar內部，且不是Menu或Dialog，則收合所有
      if (!isInsideSidebar && !isInsideActivityList && !isInsideGroupList && !isInsideMenu && !isInsideDialog) {
        setCollapseSidebar(true);
        setCollapseActivity(true);
        setCollapseGroup(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 檢測螢幕方向
  useEffect(() => {
    const checkOrientation = () => {
      const isCurrentlyPortrait = window.innerWidth < window.innerHeight;
      setIsPortrait(isCurrentlyPortrait);
    };

    // 初始檢測
    checkOrientation();

    // 監聽螢幕尺寸和方向變化
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  const selectedItem = useMemo(
    () => activities.find(a => Number(a.activity.id) === Number(selectedActivityId)),
    [activities, selectedActivityId]
  );
  const selectedActivity = selectedItem?.activity;
  const sortedGroups = useMemo(() => groups.slice().sort((a, b) => {
    const aIsUnitHall = a.isUnitHall === true;
    const bIsUnitHall = b.isUnitHall === true;
    if (aIsUnitHall === bIsUnitHall) return 0;
    return aIsUnitHall ? -1 : 1;
  }), [groups]);

  const sideW = collapseSidebar ? 56 : 240;
  const actW = collapseActivity ? 56 : 240;
  const grpW = collapseGroup ? 0 : 260;

  const getActivityInfo = async (activityId) => {
    const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {};
    const resp = await axios.get(`${url.backendHost}api/activityInfo/${activityId}`, {
      headers,
      withCredentials: true,
    });
    // 返回完整的數據，包括 Activity 關聯
    return resp.data || {};
  };

  const handleCopyInviteCode = (joinCode) => {
    if (!joinCode) return;
    navigator.clipboard.writeText(joinCode)
      .then(() => {
        alert(t('alert.codeCopied'));
      })
      .catch((err) => {
        console.error('複製邀請碼失敗:', err);
      });
  };

  const handleCopyInviteLink = (joinCode) => {
    if (!joinCode) return;
    const inviteUrl = `${url.backendHost}auth/google/invite/${joinCode}`;
    navigator.clipboard.writeText(inviteUrl)
      .then(() => {
        alert(t('alert.linkCopied'));
      })
      .catch((err) => {
        console.error('複製邀請連結失敗:', err);
      });
  };

  const handleOpenInviteMenu = (event, joinCode, group) => {
    event.stopPropagation();
    setInviteAnchorEl(event.currentTarget);
    setInviteForJoinCode(joinCode || '');
    setInviteForGroup(group || null);
  };

  const handleCloseInviteMenu = () => {
    setInviteAnchorEl(null);
    setInviteForJoinCode('');
    setInviteForGroup(null);
  };

  const handleCopyGuestLink = async () => {
    if (!inviteForGroup || !selectedActivity) return;
    const gid = inviteForGroup.id ?? inviteForGroup.groupId;
    
    try {
      // 獲取活動的 chatmode 設定
      const activityInfo = await getActivityInfo(selectedActivity.id);
      const mode = activityInfo?.settings?.chatmode || 'sticker';
      
      const guestUrl = `${window.location.origin}/guest/${selectedActivity.id}/${gid}/${mode}`;
      
      const modeNames = {
        'chatroom': t('forum.chatroom'),
        'sticker': t('forum.sticker'),
        'tagged-forum': t('forum.taggedForum'),
        'workspace': t('forum.sticker'),
      };
      await navigator.clipboard.writeText(guestUrl);
      alert(`${t('invite.copyGuest')} (${modeNames[mode] || mode})`);
    } catch (err) {
      console.error('複製訪客連結失敗:', err);
      alert(t('invite.copyGuest'));
    }
  };

  const handleEnterGroup = async (group) => {
    if (!selectedActivity || !group) return;
    const gid = group.id ?? group.groupId;
    if (!gid) return;
    setSelectedGroupId(gid);
    // 同時設置 localStorage 和 sessionStorage，確保子組件能立即讀取正確值
    localStorage.setItem('activityId', selectedActivity.id);
    sessionStorage.setItem('activityId', selectedActivity.id);
    localStorage.setItem('groupId', gid);
    sessionStorage.setItem('groupId', gid);
    if (group.joinCode) {
      localStorage.setItem('joinCode', group.joinCode);
      sessionStorage.setItem('joinCode', group.joinCode);
    }
    try {
      const info = await getActivityInfo(selectedActivity.id);
      
      localStorage.setItem('activityInfo', JSON.stringify(info));
      const mode = info?.settings?.chatmode;
      
      setEmbeddedMode(mode || 'sticker'); // 提供預設值
      // navigate(findroomByInfo({ data: info }));
    } catch (e) {
      console.error('獲取活動資訊失敗:', e);
      // 如果無法獲取 activityInfo，設定預設模式
      setEmbeddedMode('sticker');
    }
    // 樂觀更新：點擊即視為已讀，先清除標註
    markAsReadLocal(gid);
    try {
      await updateLastReadTime(gid, selectedActivity.id);
    } catch (err) {
      console.error('更新已讀時間失敗:', err);
      // 失敗時才重新查詢後端狀態
      await fetchUnreadStatus([gid]);
    }
  };

  const handleJoin = async (e) => {
    e?.preventDefault?.();
    await joinActivityByCode(joinCode);
  };

  const getIconByLabel = (label) => {
    switch (label) {
      case 'sticker':
        return RoomSettingsMindmapIcon;
      case 'chatroom':
        return RoomSettingsChatroomIcon;
      case 'tagged-forum':
        return ForumIcon;
      default:
        return null;
    }
  };

  // ❌ 移除 postActivityInfo - 對話框內部會處理
  // ❌ 移除 putActivityInfo - 對話框內部會處理

  const handleOpenSettingsMenu = async (event, act) => {
    setSettingsAnchorEl(event.currentTarget);
    setSettingsForActivityId(act.id);
    // 只在實際需要時才設置 activityId（例如在進入小組時）
    // localStorage.setItem('activityId', act.id); // 移除：開啟選單時不應該修改 localStorage
  };

  const handleCloseSettingsMenu = () => setSettingsAnchorEl(null);

  const createGroup = async (num) => {
    const activityId = settingsForActivityId;
    if (!activityId) return;
    if (num < 1) {
      alert(t('alert.minGroups'));
      return;
    }
    try {
      const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {};
      const response = await axios.get(`${url.backendHost}api/activities/${activityId}`, { headers, withCredentials: true });
      const existingGroups = response.data?.Groups || [];
      const nextGroupNumber = existingGroups.length + 1;
      for (let i = 0; i < num; i++) {
        const groupName = `第${nextGroupNumber + i}組`;
        const groupData = { groupName, activityId, numGroups: 1 };
        const res = await axios.post(`${url.backendHost}api/activities/createGroups`, groupData, { headers, withCredentials: true });
        const joinData = { userId: userId };
        await axios.put(`${url.backendHost}api/groups/${res.data.groups[0].joinCode}/join`, joinData, { headers, withCredentials: true });
      }
      
      // 創建並加入所有小組後，更新 token 以包含新的小組權限
      try { 
        const renewResponse = await axios.get(`${url.backendHost}api/users/renew`, { headers, withCredentials: true });
        if (renewResponse.data.jwtToken) {
          localStorage.setItem('jwtToken', renewResponse.data.jwtToken);
          console.log('小組創建後 JWT token 已更新');
        }
      } catch (e) {
        console.error('更新 token 失敗:', e);
      }
      
      alert(t('alert.groupsCreated', { count: num }));
      setOpenCreateGroupDialog(false);
      setNumGroupsToCreate(1);
      // refresh groups if viewing this activity
      if (selectedActivityId === activityId) {
        try {
          const res = await axios.get(`${url.backendHost}api/activities/${activityId}`, { headers, withCredentials: true });
          const data = res.data;
          const sorted = (data?.Groups || []).slice().sort((a, b) => a.groupName.localeCompare(b.groupName, 'zh-Hant', { numeric: true }));
          setGroups(sorted);
        } catch {}
      }
    } catch (error) {
      alert(t('alert.groupsFail'));
    }
  };

  const handleHideActivity = async (activityId) => {
    try {
      const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {};
      await axios.post(`${url.backendHost}api/activities/${activityId}/hide`, {}, { headers, withCredentials: true });
      alert(t('alert.activityHidden'));
      const merged = await fetchAndMergeActivities(headers);
      setActivities(merged);
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.includes('Owners cannot')) {
        alert(t('alert.cannotHideOwn'));
      } else {
        alert(t('alert.hideFail'));
      }
    }
  };

  const fetchDeletedActivities = async () => {
    try {
      const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {};
      const response = await axios.get(`${url.backendHost}api/activities/deleted/${userId}`, { headers, withCredentials: true });
      setDeletedActivities(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('獲取已刪除活動失敗:', error);
      setDeletedActivities([]);
    }
  };

  const fetchHiddenActivities = async () => {
    try {
      const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {};
      const response = await axios.get(`${url.backendHost}api/activities/hidden/${userId}`, { headers, withCredentials: true });
      setHiddenActivities(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('獲取已隱藏活動失敗:', error);
      setHiddenActivities([]);
    }
  };

  const handleRestoreActivity = async (activityId) => {
    try {
      const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {};
      await axios.post(`${url.backendHost}api/activities/${activityId}/restore`, {}, { headers, withCredentials: true });
      alert(t('alert.activityRestored'));
      await fetchDeletedActivities();
      const merged = await fetchAndMergeActivities(headers);
      setActivities(merged);
    } catch (error) {
      console.error('恢復活動失敗:', error);
      if (error.response?.status === 404) {
        alert(t('alert.restoreNotFound'));
      } else if (error.response?.status === 403) {
        alert(t('alert.restoreForbidden'));
      } else {
        alert(t('alert.restoreFail'));
      }
    }
  };

  const handleOpenRecycleBin = () => {
    setRecycleBinOpen(true);
    setLoadingDeleted(true);
    Promise.all([fetchDeletedActivities(), fetchHiddenActivities()]).finally(() => {
      setLoadingDeleted(false);
    });
  };

  const handleUnhideActivity = async (activityId) => {
    try {
      const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {};
      await axios.post(`${url.backendHost}api/activities/${activityId}/unhide`, {}, { headers, withCredentials: true });
      alert(t('alert.activityUnhidden'));
      await fetchHiddenActivities();
      const merged = await fetchAndMergeActivities(headers);
      setActivities(merged);
    } catch (error) {
      console.error('恢復隱藏活動失敗:', error);
      if (error.response?.status === 404) {
        alert(t('alert.unhideNotFound'));
      } else if (error.response?.status === 403) {
        alert(t('alert.unhideForbidden'));
      } else {
        alert(t('alert.unhideFail'));
      }
    }
  };

  const editClassSetting = () => {
    if (!settingsForActivityId) return;
    
    // ✅ 只需開啟對話框，所有資料由對話框內部載入
        setOpenSettingOfActivity(true);
  };

  const SelectedComponent = useMemo(() => {
    if (!embeddedMode) return null;
    return ModeComponents[embeddedMode] || ModeComponents['sticker'];
  }, [embeddedMode]);

  return (
    <>
      {/* 直式螢幕提示覆蓋層 */}
      {isPortrait && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          padding: '20px',
          textAlign: 'center',
        }}>
          <div style={{
            animation: 'rotate-device 2s ease-in-out infinite',
            marginBottom: '30px',
          }}>
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
              <line x1="12" y1="18" x2="12" y2="18"/>
            </svg>
          </div>
          <h2 style={{ fontSize: '24px', marginBottom: '16px', fontWeight: '600' }}>
            {t('portrait.title')}
          </h2>
          <p style={{ fontSize: '16px', color: '#aaa', maxWidth: '300px' }}>
            {t('portrait.description')}
          </p>
          <style dangerouslySetInnerHTML={{
            __html: `
              @keyframes rotate-device {
                0%, 100% { transform: rotate(0deg); }
                25% { transform: rotate(-90deg); }
                75% { transform: rotate(90deg); }
              }
            `
          }} />
        </div>
      )}
      
      <div className="main-layout" style={{ gridTemplateColumns: (currentView === 'partners' || currentView === 'templates' || currentView === 'studio') ? `${sideW}px 1fr` : `${sideW}px ${actW}px ${grpW}px 1fr` }}>
      <aside 
        className={`sidebar ${collapseSidebar ? 'collapsed' : ''}`}
        onClick={() => setCollapseSidebar(false)}
      >
        <div className="user">
          <div className="avatar" style={{ color: '#eaeaea' }}>
            <img src={siteLogo} alt="site logo" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
          </div>
          <div className="name" style={{ color: '#eaeaea' }}>{user?.name || 'User'}</div>
        </div>
        <nav className="menu">
          <button className={`menu-item ${currentView === 'templates' ? 'active' : ''}`} onClick={() => setCurrentView('templates')} title={t('nav.quickStart')}><span className="icon"><Zap size={20} /></span><span className="text">{t('nav.quickStart')}</span></button>
          <button className={`menu-item ${currentView === 'activity' ? 'active' : ''}`} onClick={() => setCurrentView('activity')}><span className="icon"><Calendar size={20} /></span><span className="text">{t('nav.activity')}</span></button>
          <button className={`menu-item ${currentView === 'partners' ? 'active' : ''}`} onClick={() => setCurrentView('partners')}><span className="icon"><Bot size={20} /></span><span className="text">{t('nav.partners')}</span></button>
          <button className={`menu-item ${currentView === 'studio' ? 'active' : ''}`} onClick={() => setCurrentView('studio')}><span className="icon"><Wrench size={20} /></span><span className="text">{t('nav.studio')}</span></button>
          {/* <button className="menu-item"><span className="icon"><Users size={20} /></span><span className="text">People</span></button> */}
          <button className="menu-item" onClick={() => navigate('/settings')}><span className="icon"><Settings size={20} /></span><span className="text">{t('nav.settings')}</span></button>
          <div style={{ marginTop: 'auto', padding: '12px 8px' }}>
            <select
              value={i18n.language}
              onChange={(e) => handleLanguageChange(e.target.value)}
              style={{
                width: collapseSidebar ? '32px' : '100%',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '6px',
                color: '#eaeaea',
                padding: collapseSidebar ? '4px 2px' : '6px 8px',
                fontSize: '12px',
                cursor: 'pointer',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
              title={t('language.switch')}
            >
              {SUPPORTED_LANGUAGES.map(({ code, label }) => (
                <option key={code} value={code} style={{ background: '#333', color: '#fff' }}>
                  {collapseSidebar ? code.split('-')[0].toUpperCase() : label}
                </option>
              ))}
            </select>
          </div>
        </nav>
      </aside>

      {currentView === 'activity' && (
        <section 
          className={`activity-list ${collapseActivity ? 'collapsed' : ''}`}
          onClick={() => setCollapseActivity(false)}
        >
        <div className="section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {!collapseActivity && <span className="section-title" style={{color: colors.primary.background}}>{t('activity.space')}</span>}
          {!collapseActivity && (
            <>
              <CreateActivityForm
                callback_setActivities={(updater) => {
                  setActivities(prev => {
                    if (typeof updater === 'function') {
                      // 如果updater是函數，調用它並處理結果（向後兼容）
                      const nextActivitiesArray = updater(prev.map(a => a.activity));
                      const currentUserId = Number(userId);
                      const mergedMap = new Map(prev.map(item => [item.activity.id, item]));
                      
                      for (const act of nextActivitiesArray) {
                        const existing = mergedMap.get(act.id) || { 
                          activity: act, 
                          joinedGroups: [], 
                          allGroups: [], 
                          isOwner: act.userId === currentUserId 
                        };
                        existing.activity = act;
                        if (Array.isArray(act.Groups)) {
                          existing.allGroups = act.Groups;
                          if (existing.isOwner && existing.allGroups.length > 0) {
                            existing.joinedGroups = existing.allGroups.slice();
                          }
                        }
                        mergedMap.set(act.id, existing);
                      }
                      return Array.from(mergedMap.values())
                        .sort((a, b) => new Date(b.activity.createdAt) - new Date(a.activity.createdAt));
                    } else {
                      // 如果updater是數組，直接使用它（完整的活動列表）
                      console.log('使用完整的活動列表，共', updater.length, '個活動');
                      return updater;
                    }
                  });
                }}
                renderTrigger={(openDialog) => (
                  <div
                    onClick={openDialog}
                    title="新增活動"
                    style={{
                      padding: 4,
                      backgroundColor: 'rgba(255,255,255,0.14)',
                      border: '1px solid rgba(255,255,255,0.35)',
                      borderRadius: 6,
                      color: '#ffffff',
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <AddIcon fontSize="small" htmlColor="#ffffff" />
                  </div>
                )}
              />
              <div
                onClick={() => setJoinOpen(true)}
                title="加入探究活動"
                style={{
                  padding: 4,
                  backgroundColor: 'rgba(255,255,255,0.14)',
                  border: '1px solid rgba(255,255,255,0.35)',
                  borderRadius: 6,
                  color: '#ffffff',
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <SearchCode size={16} color="#ffffff" />
              </div>
            </>
          )}
          </div>
        </div>
        {loading && <div className="loading">{t('activity.loading')}</div>}
        {!loading && activities.length === 0 && (
          <div className="empty">{t('activity.empty')}</div>
        )}
        {!loading && activities.map(item => {
          const act = item.activity;
          const joinedCount = item.joinedGroups?.length || 0;
          const allCount = item.allGroups?.length || 0;
          const isSelected = Number(selectedActivityId) === Number(act.id);
          const activityHasUnread = (item.joinedGroups || []).some((g) => {
            const gid = g.id ?? g.groupId;
            return gid && hasUnreadMap[gid];
          });
          const sub = item.isOwner
            ? t('activity.ownerGroupCount', { count: allCount })
            : (joinedCount > 0 ? t('activity.joinedGroupCount', { count: joinedCount }) : t('activity.ownerGroupCount', { count: allCount }));
          return (
            <button
              key={act.id}
              className={`activity-item ${isSelected ? 'selected' : ''}`}
              onClick={() => { setSelectedActivityId(act.id); setCollapseGroup(false); }}
              style={activityHasUnread ? { border: '1px solid #ff5252' } : undefined}
            >
              <div className="cover">{(act.title || '').slice(0, 1) || 'A'}</div>
              <div className="meta">
                <div className="title" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {act.title}
                  <UnreadIndicator active={activityHasUnread} />
                </div>
                <div className="sub">{sub}</div>
              </div>
              {!collapseActivity && (
                <>
                  {item.isOwner ? (
                    <span
                      title="設定"
                      onClick={(e) => { e.stopPropagation(); handleOpenSettingsMenu(e, act); }}
                      style={{ marginLeft: 'auto', cursor: 'pointer', color: '#AAA', display: 'flex', alignItems: 'center' }}
                      className="activity-settings-icon"
                    >
                      <Settings size={20} />
                    </span>
                  ) : (
                    <span
                      title="隱藏活動"
                      onClick={(e) => { e.stopPropagation(); handleHideActivity(act.id); }}
                      style={{ marginLeft: 'auto', cursor: 'pointer', color: '#AAA', display: 'flex', alignItems: 'center' }}
                      className="activity-hide-icon"
                    >
                      <EyeOff size={20} />
                    </span>
                  )}
                </>
              )}
            </button>
          );
        })}
        {!collapseActivity && (
          <div style={{ 
            padding: '12px 16px', 
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            marginTop: 'auto',
            textAlign: 'center'
          }}>
            <button
              onClick={handleOpenRecycleBin}
              style={{
                background: 'none',
                border: 'none',
                color: '#888',
                fontSize: '12px',
                cursor: 'pointer',
                padding: '4px 8px',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
                opacity: 0.7,
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '1'}
              onMouseLeave={(e) => e.target.style.opacity = '0.7'}
              title={t('activity.recycleBin')}
            >
              {t('activity.recycleBin')}
            </button>
          </div>
        )}
        </section>
      )}

      {currentView === 'activity' && (
        <section 
          className={`group-list ${collapseGroup ? 'collapsed-group' : ''}`}
          onClick={() => setCollapseGroup(false)}
        >
        <div className="section-header">
          <span className="section-title" style={{color: colors.primary.background}}>{t('group.rooms')}</span>
        </div>
        {selectedActivity && sortedGroups.map(g => {
          const gid = g.id ?? g.groupId;
          const hasUnread = Boolean(hasUnreadMap[gid]);
          const isUnitHall = g.isUnitHall === true;
          return (
            <button
              key={gid}
              className={`group-item ${isUnitHall ? 'unit-hall' : ''}`}
              onClick={() => handleEnterGroup(g)}
              style={hasUnread ? { border: '1px solid #ff5252' } : undefined}
            >
              <div className="group-title" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {g.groupName}
                <UnreadIndicator active={hasUnread} />
              </div>
              <div className="group-sub" style={{ width: '100%' }}>
                <span>{Array.isArray(g.userId) ? t('group.count', { count: g.userId.length }) : t('group.count', { count: 0 })}</span>
                {g.joinCode && (
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#777', fontSize: '12px' }}>{g.joinCode}</span>
                    <IconButton
                      size="small"
                      onClick={(e) => handleOpenInviteMenu(e, g.joinCode, g)}
                      style={{ color: '#cfd3ff', padding: '4px' }}
                      title="分享邀請"
                    >
                      <Share2 size={16} />
                    </IconButton>
                  </div>
                )}
              </div>
            </button>
          );
        })}
        </section>
      )}

      <Dialog open={joinOpen} onClose={() => setJoinOpen(false)}>
        <DialogTitle>{t('join.title')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('join.label')}
            type="text"
            fullWidth
            variant="standard"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJoinOpen(false)}>{t('join.cancel')}</Button>
          <Button onClick={handleJoin}>{t('join.confirm')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={exploreDetailOpen} onClose={() => setExploreDetailOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 800 }}>
          {selectedExploreActivity?.title || '活動預覽'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>活動說明</Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {selectedExploreActivity?.preview?.activityDescription || selectedExploreActivity?.content || '沒有活動說明'}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>初始節點</Typography>
              {Array.isArray(selectedExploreActivity?.preview?.initialNodes) && selectedExploreActivity.preview.initialNodes.length > 0 ? (
                <Stack spacing={1.5}>
                  {selectedExploreActivity.preview.initialNodes.map((node, index) => (
                    <Box key={`${node.title || 'node'}-${index}`} sx={{ p: 1.5, borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)', backgroundColor: 'rgba(0,0,0,0.02)' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {formatPreviewNodeText(node.title) || `節點 ${index + 1}`}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {formatPreviewNodeText(node.content) || '無內容'}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">目前沒有初始節點</Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExploreDetailOpen(false)}>{t('join.cancel')}</Button>
          <Button
            variant="contained"
            onClick={async () => {
              const code = selectedExploreActivity?.defaultJoinCode || selectedExploreActivity?.groups?.[0]?.joinCode;
              if (!code) {
                alert('此活動目前沒有可加入的小組');
                return;
              }
              await joinActivityByCode(code, selectedExploreActivity?.id);
              setExploreDetailOpen(false);
            }}
            disabled={!selectedExploreActivity?.defaultJoinCode && !(selectedExploreActivity?.groups?.length > 0)}
          >
            加入活動
          </Button>
        </DialogActions>
      </Dialog>

      <Menu 
        anchorEl={settingsAnchorEl} 
        open={Boolean(settingsAnchorEl)} 
        onClose={handleCloseSettingsMenu}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={(e) => { e.stopPropagation(); handleCloseSettingsMenu(); navigate('/teacher/pageOfPrepareLesson'); }}>{t('settings.assignTask')}</MenuItem>
        <MenuItem onClick={(e) => { e.stopPropagation(); handleCloseSettingsMenu(); setOpenCreateGroupDialog(true); }}>{t('settings.addGroup')}</MenuItem>
        <MenuItem onClick={(e) => { e.stopPropagation(); handleCloseSettingsMenu(); editClassSetting(); }}>{t('settings.chatSetting')}</MenuItem>
        <MenuItem onClick={(e) => { e.stopPropagation(); handleCloseSettingsMenu(); navigate(`/dashboard?activityId=${settingsForActivityId}`); }}>{t('settings.dashboard')}</MenuItem>
      </Menu>

      <Menu 
        anchorEl={inviteAnchorEl} 
        open={Boolean(inviteAnchorEl)} 
        onClose={handleCloseInviteMenu}
        PaperProps={{
          style: { minWidth: '280px' }
        }}
      >
        <Box sx={{ padding: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          <Box sx={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>
            {t('invite.code', { code: inviteForJoinCode || '-' })}
          </Box>
          {inviteForJoinCode && (
            <Box sx={{ padding: '12px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
              <QRCodeSVG 
                value={`${url.backendHost}auth/google/invite/${inviteForJoinCode}`}
                size={180}
                level="M"
                includeMargin={true}
              />
            </Box>
          )}
          <Box sx={{ fontSize: '12px', color: '#666', textAlign: 'center' }}>
            {t('invite.scanQR')}
          </Box>
        </Box>
        <MenuItem onClick={() => { handleCopyInviteCode(inviteForJoinCode); handleCloseInviteMenu(); }}>
          {t('invite.copyCode')}
        </MenuItem>
        <MenuItem onClick={() => { handleCopyInviteLink(inviteForJoinCode); handleCloseInviteMenu(); }}>
          {t('invite.copyLink')}
        </MenuItem>
        <Box sx={{ borderTop: '1px solid #e0e0e0', my: 1 }} />
        <MenuItem onClick={() => { handleCopyGuestLink(); handleCloseInviteMenu(); }}>
          {t('invite.copyGuest')}
        </MenuItem>
      </Menu>

      <Dialog open={openCreateGroupDialog} onClose={() => setOpenCreateGroupDialog(false)}>
        <DialogTitle>{t('group.addGroup')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('group.groupCount')}
            type="number"
            fullWidth
            variant="standard"
            value={numGroupsToCreate}
            onChange={(e) => setNumGroupsToCreate(parseInt(e.target.value || '0', 10))}
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateGroupDialog(false)}>{t('join.cancel')}</Button>
          <Button onClick={async () => { setCreatingGroups(true); await createGroup(numGroupsToCreate); setCreatingGroups(false); }}>
            {creatingGroups ? '...' : t('join.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <ActivitySettingsDialog
        open={openSettingOfActivity}
        onClose={() => {
          setOpenSettingOfActivity(false);
        }}
        activityId={settingsForActivityId}
        onActivityDeleted={() => {
          // 刷新活动列表
          window.location.reload();
        }}
      />

      {/* 回收桶對話框 */}
      <Dialog
        open={recycleBinOpen}
        onClose={() => setRecycleBinOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Trash2 size={24} />
            <span>{t('recycleBin.title')}</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingDeleted ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>{t('recycleBin.loading')}</div>
          ) : deletedActivities.length === 0 && hiddenActivities.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              {t('recycleBin.empty')}
            </div>
          ) : (
            <Box>
              <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                {deletedActivities.length > 0 && (
                  <Box sx={{ marginBottom: '24px' }}>
                    <div style={{ marginBottom: '12px', color: '#666', fontSize: '0.9rem', fontWeight: '500' }}>
                      {t('recycleBin.deleted', { count: deletedActivities.length })}
                    </div>
                    {deletedActivities.map((activity) => (
                      <Box
                        key={`deleted-${activity.id}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px',
                          marginBottom: '8px',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          backgroundColor: '#f9f9f9'
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                            {activity.title}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#666' }}>
                            {t('recycleBin.deletedAt', { time: new Date(activity.updatedAt).toLocaleString(i18n.language) })}
                          </div>
                        </Box>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<RotateCcw size={16} />}
                          onClick={() => handleRestoreActivity(activity.id)}
                          sx={{
                            backgroundColor: '#667eea',
                            '&:hover': {
                              backgroundColor: '#5568d3'
                            }
                          }}
                        >
                          {t('recycleBin.restore')}
                        </Button>
                      </Box>
                    ))}
                  </Box>
                )}

                {hiddenActivities.length > 0 && (
                  <Box>
                    <div style={{ marginBottom: '12px', color: '#666', fontSize: '0.9rem', fontWeight: '500' }}>
                      {t('recycleBin.hidden', { count: hiddenActivities.length })}
                    </div>
                    {hiddenActivities.map((activity) => (
                      <Box
                        key={`hidden-${activity.id}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px',
                          marginBottom: '8px',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          backgroundColor: '#f9f9f9'
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                            {activity.title}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#666' }}>
                            {t('recycleBin.hiddenAt', { time: new Date(activity.hideTime || activity.updatedAt).toLocaleString(i18n.language) })}
                          </div>
                        </Box>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<RotateCcw size={16} />}
                          onClick={() => handleUnhideActivity(activity.id)}
                          sx={{
                            backgroundColor: '#667eea',
                            '&:hover': {
                              backgroundColor: '#5568d3'
                            }
                          }}
                        >
                          {t('recycleBin.restore')}
                        </Button>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecycleBinOpen(false)}>{t('recycleBin.close')}</Button>
        </DialogActions>
      </Dialog>

      <main className="workspace">
        {currentView === 'templates' ? (
            <Homepage
              onActivityCreated={async (result) => {
                try {
                  const headers = jwtToken ? { Authorization: ['Bearer', jwtToken].join(' ') } : undefined;
                  const merged = await fetchAndMergeActivities(headers);
                  setActivities(merged);
                  if (result?.activity?.id) setSelectedActivityId(result.activity.id);
                  setCurrentView('activity');
                } catch (error) {
                  console.error('刷新活動列表失敗:', error);
                }
              }}
              onViewChange={setCurrentView}
              showExploreActivities
              exploreLoading={exploreLoading}
              exploreError={exploreError}
              exploreSortBy={exploreSortBy}
              onExploreSortChange={(event) => setExploreSortBy(event.target.value)}
              sortedExploreActivities={sortedExploreActivities}
              onExploreActivityClick={openExploreActivity}
            />
        ) : currentView === 'partners' ? (
          <PartnerManager 
            onChatCreated={(chatData) => {
              // 當夥伴聊天創建成功時，可以在這裡處理相關邏輯
              
            }}
          />
        ) : currentView === 'studio' ? (
          <Studio userId={userId} />
        ) : (
          <>
            {SelectedComponent ? null : (
              selectedActivity ? (
                <div className="activity-details">
                  <div className="activity-title">{selectedActivity.title}</div>
                  <div className="activity-meta">
                    {/* <div>開始：{new Date(selectedActivity.startDate).toLocaleDateString('zh-TW')}</div>
                    <div>結束：{new Date(selectedActivity.endDate).toLocaleDateString('zh-TW')}</div> */}
                  </div>
                </div>
              ) : (
                <div className="empty">{t('workspace.selectActivity')}</div>
              )
            )}

            {SelectedComponent && (
              <div className="embedded-forum">
                <Suspense fallback={<div className="loading">{t('workspace.loading')}</div>}>
                  <SelectedComponent key={`${selectedActivity?.id || ''}:${selectedGroupId || ''}:${embeddedMode || ''}`} showNavbar={false} />
                </Suspense>
              </div>
            )}
          </>
        )}
      </main>
    </div>
    </>
  );
}